import type { SubCategoryData } from '@/components/layouts/sub-category-layout/type'
import type { ProductionFilterState } from '@/components/pages/production/components/context/types'
import { CATEGORY_IDS } from '@/constants'
import { UserCurrency, UserLocation } from '@/types/app-types'
import type { DisplayCurrencyEventType, GeolocationEventType } from '@/utils/analytics/types'

import {
    ERROR_EVENT_NAME,
    HOME_PAGE_INTERACTION,
    HOME_PAGE_INTERACTION_CONTENT_TYPE,
    MARKETING_VISIT_EVENT_NAME,
    PAGE_VIEW_EVENT_NAME,
    VIEW_PROMOTION_EVENT_NAME,
    SEARCH_EVENT_NAME,
    GEOLOCATION_EVENT_NAME,
    DISPLAY_CURRENCY_EVENT_NAME,
    LANDING_PAGE_VIEW_EVENT_NAME,
} from './constants'

export type DataLayerEvent =
    | GAInitializationEvent
    | GAUserPropertiesEvent
    | GAHomePageEvent
    | PageViewEvent
    | SearchEvent
    | ViewPromotionEvent
    | FilterEvent
    | GAErrorEvent
    | MarketingVisitEvent
    | GAECommerceEvent
    | GAOptimizelyExperimentEvent
    | GABrazeEvent
    | GeolocationEvent
    | LandingPageViewEvent
    | GACheckoutCompleteEvent
    | DisplayCurrencyEvent

declare global {
    interface Window {
        dataLayer: DataLayerEvent[]
    }
}

export enum FILTER_ACTION {
    CLICKED = 'clicked',
    APPLIED = 'applied',
}

export enum FILTER_TYPE {
    DATE_FILTER_CLICKED = 'date filter button',
    SINGLE_DATE = 'single date',
    DATE_RANGE = 'date range',
    MAP_SECTION = 'map section',
    MAP_ROW = 'map row',
}

export type Categories = Lowercase<keyof typeof CATEGORY_IDS>

/*
    Server Information common to all events
 */
export interface ServerInfo {
    affiliation: 'Vivid Seats Web' // google - "Vivid Seats Web" / "App" / "TFS" / "Distribution"
    affiliation_detail?: string // Web=checkout type, App=App name, TFS=partner, Distro=partner
    affiliation_id?: string // TFS wsUserId
    affiliation_variation?: string // TFS wsUserVar
    build: string
    environment: string
}

/*
    User Information common to all events
 */
export interface UserInfo {
    is_returning_customer?: boolean
    user_id?: string
    crm_id?: string
    email?: string
    user_location?: UserLocation
    display_currency?: UserCurrency
}

/**
 * Product information
 */

export interface PerformerItem {
    performer: string
    performer_id: string
    performer2?: string
    performer_id_2?: string
    item_category?: string
    item_category2?: string
    item_category3?: string
}

export interface ProductionItem extends PerformerItem {
    production_id: string
    production_city: string
    production_state: string
    days_until_production: number
    production?: string
    production_datetime?: string
    distance_to_production?: number
    total_inventory_available?: number
    price_group?: string
}

export interface VenueItem {
    venue?: string
    venue_id: string
    venue_name?: string
}

export interface GAItem {
    item_name: string
    item_id: string
    item_list_name: string | null

    item_type: 'Tickets' | 'Parking' | 'Performer' // Tickets | Parking | Performer
    item_category: string // EventType
    item_category2?: string // EventCategory
    item_category3?: string // Performer/Team
    item_category4?: string // Venue Name
    item_category5?: string // Production Date
}

/*
   GA4 Events
 */
export interface GAEvent {
    event?: string
}

export interface GABrazeEvent {
    event: 'braze'
    trigger_id: string
    message_id: string
}

export interface GAOptimizelyExperimentEvent {
    event: 'optimizely'
    experiment: string
    variation: string
}

export interface GAInitializationEvent {
    server: ServerInfo
    user: UserInfo
}

export interface GAUserPropertiesEvent {
    user: UserInfo
}

export interface GAECommerceEvent extends GAEvent {
    ecommerce: unknown | null
}

export interface GASelectContentEvent extends GAEvent {
    content_type?: string
}

export interface GACheckoutCompleteEvent extends GAEvent {
    event: 'sa-checkout-complete'
}

export interface MarketingVisitEvent extends GAEvent {
    event: typeof MARKETING_VISIT_EVENT_NAME
    utm: {
        medium?: string
        source?: string
        campaign?: string
        term?: string
        adgroupId?: string
        target?: string
        audienceId?: string
        keywordId?: string
        content?: string
        promo?: string
    }
}

export interface PageViewEvent extends GAEvent {
    event: typeof PAGE_VIEW_EVENT_NAME
    url_path: string
    page_type: string
    page?: VenueItem | PerformerItem | ProductionItem | SubCategoryData
    title?: string
}

export interface LandingPageViewEvent extends GAEvent {
    event: typeof LANDING_PAGE_VIEW_EVENT_NAME
    url_path: string
}

export interface GAHomePageEvent extends GAEvent {
    event: typeof HOME_PAGE_INTERACTION
    content_type: typeof HOME_PAGE_INTERACTION_CONTENT_TYPE
    click_location: string
    click_text: string
    click_tile_location?: number
    carousel_location?: number
}

// Click text and search term can be null when a user is opening search
export interface SearchEvent extends GAEvent {
    event: typeof SEARCH_EVENT_NAME
    search_term?: string
    page_location?: string
}

export interface GeolocationEvent extends GAEvent {
    event: typeof GEOLOCATION_EVENT_NAME
    page_type: string
    event_type: GeolocationEventType
    search_term?: string
}

export interface DisplayCurrencyEvent extends GAEvent {
    event: typeof DISPLAY_CURRENCY_EVENT_NAME
    event_type: DisplayCurrencyEventType
    display_currency?: string
}

export interface ViewPromotionEvent extends GAEvent {
    event: typeof VIEW_PROMOTION_EVENT_NAME
    creative_name?: string
    items: GAItem[] | ECommerceItem[]
    location_id?: string
    promotion_id?: string
    promotion_name?: string
}

export interface FilterEvent extends GAEvent {
    page_type: string
    filter: FILTER_TYPE | ProductionFilterState
    action?: FILTER_ACTION
    sort_by?: string
    item_count?: number
}

export interface GAErrorEvent extends GAEvent {
    event: typeof ERROR_EVENT_NAME
    error_type: string
    error_message: string
    error_code: string
    error_url: string
}

export interface CheckoutECommerceEvent extends GAECommerceEvent {
    user?: UserInfo
    ecommerce: {
        // used in:
        //   begin_checkout
        //   purchase
        price_group?: string
        // used in:
        //   begin_checkout - YES
        //   add_to_cart - YES
        //   checkout_login - NO
        //   add_shipping_info - YES
        //   add_payment_info - YES
        //   select_insurance - NO
        //   purchase - YES
        items?: SelectListingItem[]
        // order-level totals
        // used in:
        //    begin_checkout
        //    purchase
        currency?: string // google
        subtotal?: number // CUSTOM - sum of items.extended_price
        shipping?: number // google - delivery fee
        service_fee?: number // CUSTOM - service charge
        tax?: number // google - sales tax
        discount?: number // google - sum of promo codes
        value?: number // google - conversion value, no insurance, no tax
        total?: number // CUSTOM - total cost to customer, no insurance because it is billed separately

        // used in:
        //   checkout_login
        //   purchase
        is_new_account?: boolean // bool - is this a new signup? - only after the login portion of checkout

        // add_shipping_info ONLY
        shipping_tier?: string // google, delivery_method

        // add_payment_info ONLY
        payment_type?: string // google - ALL CAPS - CREDIT_CARD/PAYPAL/APPLE_PAY

        // select_insurance ONLY
        insurance_attached?: boolean // CUSTOM
        insurance_template?: string // CUSTOM

        // purchase ONLY
        transaction_id?: number // google - order id
        insurance_amount?: number // CUSTOM - insurance
        store_credit_amount?: number // CUSTOM - sum of store credits used
        gift_card_amount?: number // CUSTOM - sum of gift cards used
        loyalty_amount?: number // CUSTOM - sum of loyalty amount burned
        coupon?: string // google - promo code used
        promo_amount?: number // CUSTOM - sum of promo codes used - same as discount
        payment_amount?: number // CUSTOM - amount charged to customer from us (does not include insurance)
        production_previously_purchased?: boolean // CUSTOM
    }
}
export interface CheckoutGiftCardPayment {
    code: string
    maskedCode: string
    chargedAmount: number
    reason: string
    storeCredit: boolean
}

// view_item
export interface ViewProductionItem extends ECommerceItem {
    total_inventory_available?: number
    production_proximity?: number
}

// select_content:select_ticket
export interface SelectListingItem extends ViewProductionItem {
    seat_tickets_available?: number

    item_variant: string // section

    quantity?: number
    price: number
    extended_price?: number // price * quantity

    stock_type: string
    ticket_section: string
    ticket_row: string
    ticket_id?: string
    index?: number
}
// select_item
export interface SelectProductionItem extends ECommerceItem {
    index: number
}
// view_item_list
export interface ViewProductionListItem extends ECommerceItem {
    index: number
}
/**
 * E-commerce Events - these are the data structures that are passed to GA
 */
export interface ViewProductionListEvent extends GAECommerceEvent {
    ecommerce: {
        url_path: string | null
        item_list_name: string | null
        price_group?: string
        items: ViewProductionListItem[]
    }
}

export interface SelectProductionEvent extends GAECommerceEvent {
    ecommerce: {
        item_list_name: string | null
        price_group?: string
        items: SelectProductionItem[]
    }
}

export interface SelectSearchSuggestionEvent extends GAECommerceEvent {
    ecommerce: {
        items: {
            name: string
            pageType: string
            category?: string
            pageLocation: string
            index: number
        }
    }
    search_suggestion_group: string
}

export interface SelectFocusSearchSuggestionEvent extends GAECommerceEvent {
    ecommerce: {
        items: {
            title: string
            pageLocation: string
            index: number
        }
    }
    search_suggestion_group: string
}

export interface ViewProductionEvent extends GAECommerceEvent {
    content_type: string
    ecommerce: {
        price_group?: string
        items: ViewProductionItem[]
        transacting_currency: string
    }
}

export type SelectTicketEvent = ViewProductionEvent

/**
 * E-commerce Items - this is the representation of Productions in GA, with some additional context fields depending on the event
 */
export type ECommerceItem = ProductionItem & VenueItem & PerformerItem & GAItem
